export const getMissingPayLabel = (source?: string | null): string => (
  source?.toLowerCase() === 'stagecrew'
    ? 'Pay not provided'
    : 'Pay not captured in StageCrew; confirm on original listing'
);

interface CompactJobPay {
  rate?: string | null;
  rate_min?: number | string | null;
  rate_max?: number | string | null;
  rate_type?: string | null;
  source?: string | null;
}

const COMPACT_RATE_UNITS: Record<string, string> = {
  hour: '/hr',
  hourly: '/hr',
  hr: '/hr',
  day: '/day',
  daily: '/day',
  week: '/wk',
  weekly: '/wk',
  weekly_salary: '/wk',
  month: '/month',
  monthly: '/month',
  mo: '/month',
  flat: ' total',
  project: ' total',
  salary: '/yr',
  year: '/yr',
  yearly: '/yr',
};

const finiteRate = (value?: number | string | null): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? value : null;
  }
  if (typeof value !== 'string' || !/^\d+(?:\.\d+)?$/.test(value.trim())) return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : null;
};

/** Compact pay copy for public listing cards. Structured values win only when valid. */
export const formatCompactJobPay = (job: CompactJobPay): string => {
  const minimum = finiteRate(job.rate_min);
  const maximum = finiteRate(job.rate_max);
  const normalizedType = String(job.rate_type || '').toLowerCase();
  const unit = COMPACT_RATE_UNITS[normalizedType] || '';
  const formatAmount = (value: number) => Number(value).toLocaleString();
  const rawRate = typeof job.rate === 'string' ? job.rate.trim() : '';

  if (minimum !== null && maximum !== null && minimum > maximum) {
    return rawRate || getMissingPayLabel(job.source);
  }

  if (minimum !== null && maximum !== null && minimum !== maximum) {
    return `$${formatAmount(minimum)}-$${formatAmount(maximum)}${unit}`;
  }
  if (minimum !== null && maximum === null) {
    return `Starting at $${formatAmount(minimum)}${unit}`;
  }
  if (minimum === null && maximum !== null) {
    return `Up to $${formatAmount(maximum)}${unit}`;
  }
  if (minimum !== null) {
    return `$${formatAmount(minimum)}${unit}`;
  }

  return rawRate || getMissingPayLabel(job.source);
};
